import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Play, Square, Pencil, BarChart3 } from 'lucide-react';
import { atlasApi, AtlasUser, AvaliacaoCiclo, AvaliacaoFormulario, AvaliacaoCicloTipo } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  onVerResultado: (cicloId: string, funccodi: number) => void;
}

const STATUS: Record<string, { label: string; classes: string }> = {
  rascunho: { label: 'Rascunho', classes: 'bg-gray-100 text-gray-600' },
  ativo: { label: 'Ativo', classes: 'bg-emerald-100 text-emerald-700' },
  encerrado: { label: 'Encerrado', classes: 'bg-rose-100 text-rose-700' },
};

const TIPO_LABEL: Record<AvaliacaoCicloTipo, string> = {
  '90dias': '90 dias', '180dias': '180 dias', '360': '360°', anual: 'Anual',
};

const toCsv = (arr?: string[]) => (arr || []).join(', ');
const fromCsv = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

interface FormState {
  nome: string;
  tipo: AvaliacaoCicloTipo;
  data_inicio: string;
  data_fim: string;
  formulario_id: string;
  departamentos: string;
  cargos: string;
  inclui: { autoavaliacao: boolean; gestor: boolean; pares: boolean; liderados: boolean };
}

const vazio = (formularioId = ''): FormState => ({
  nome: '', tipo: '360', data_inicio: '', data_fim: '', formulario_id: formularioId,
  departamentos: '', cargos: '',
  inclui: { autoavaliacao: true, gestor: true, pares: false, liderados: false },
});

const Ciclos: React.FC<Props> = ({ user, onVerResultado }) => {
  const [ciclos, setCiclos] = useState<AvaliacaoCiclo[]>([]);
  const [formularios, setFormularios] = useState<AvaliacaoFormulario[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(vazio());
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    Promise.all([atlasApi.getCiclosAval(user), atlasApi.getFormularios(user)])
      .then(([cs, fs]) => { setCiclos(cs.itens || []); setFormularios(fs.itens || []); })
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const nomeFormulario = (id: string) => formularios.find(f => f._id === id)?.nome || '—';

  const abrirNovo = () => { setEditId(null); setForm(vazio(formularios[0]?._id || '')); setFb(null); setModalAberto(true); };

  const abrirEdicao = (c: AvaliacaoCiclo) => {
    setEditId(c._id);
    setForm({
      nome: c.nome || '',
      tipo: c.tipo || '360',
      data_inicio: c.data_inicio || '',
      data_fim: c.data_fim || '',
      formulario_id: c.formulario_id || '',
      departamentos: toCsv(c.escopo?.departamentos),
      cargos: toCsv(c.escopo?.cargos),
      inclui: {
        autoavaliacao: c.inclui?.autoavaliacao ?? true,
        gestor: c.inclui?.gestor ?? true,
        pares: c.inclui?.pares ?? false,
        liderados: c.inclui?.liderados ?? false,
      },
    });
    setFb(null);
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setFb({ tipo: 'erro', msg: 'Nome é obrigatório.' }); return; }
    if (!form.formulario_id) { setFb({ tipo: 'erro', msg: 'Selecione um formulário.' }); return; }
    setSalvando(true);
    setFb(null);
    const body = {
      nome: form.nome.trim(),
      tipo: form.tipo,
      data_inicio: form.data_inicio || undefined,
      data_fim: form.data_fim || undefined,
      formulario_id: form.formulario_id,
      escopo: { departamentos: fromCsv(form.departamentos), cargos: fromCsv(form.cargos) },
      inclui: form.inclui,
    };
    try {
      if (editId) await atlasApi.editarCicloAval(user, editId, body);
      else await atlasApi.criarCicloAval(user, body);
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const acao = async (fn: Promise<any>) => {
    try { await fn; carregar(); } catch (e: any) { setErro(e.message || 'Erro'); }
  };

  const incluiResumo = (c: AvaliacaoCiclo): string => {
    const partes: string[] = [];
    if (c.inclui?.autoavaliacao) partes.push('auto');
    if (c.inclui?.gestor) partes.push('gestor');
    if (c.inclui?.pares) partes.push('pares');
    if (c.inclui?.liderados) partes.push('liderados');
    return partes.join(', ') || '—';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex-1">Ciclos de avaliação</h2>
        <button onClick={abrirNovo} disabled={formularios.length === 0} title={formularios.length === 0 ? 'Crie um formulário primeiro' : undefined} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> Novo ciclo
        </button>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}
      {formularios.length === 0 && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">Crie ao menos um formulário antes de abrir um ciclo.</div>
      )}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : ciclos.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhum ciclo criado.</div>
      ) : (
        <ul className="space-y-2">
          {ciclos.map(c => {
            const st = STATUS[c.status] || { label: c.status, classes: 'bg-gray-100 text-gray-500' };
            return (
              <li key={c._id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{c.nome}</div>
                  <div className="text-xs text-gray-400">{TIPO_LABEL[c.tipo] || c.tipo} · {nomeFormulario(c.formulario_id)} · inclui: {incluiResumo(c)}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.classes}`}>{st.label}</span>
                {c.status === 'rascunho' && (
                  <>
                    <button onClick={() => abrirEdicao(c)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium flex items-center gap-1 hover:border-purple-300"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button onClick={() => acao(atlasApi.publicarCicloAval(user, c._id))} className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium flex items-center gap-1 hover:bg-emerald-100"><Play className="w-3.5 h-3.5" /> Publicar</button>
                  </>
                )}
                {c.status === 'ativo' && (
                  <button onClick={() => acao(atlasApi.encerrarCicloAval(user, c._id))} className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-medium flex items-center gap-1 hover:bg-rose-100"><Square className="w-3.5 h-3.5" /> Encerrar</button>
                )}
                {c.status === 'encerrado' && (
                  <button onClick={() => onVerResultado(c._id, 0)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium flex items-center gap-1 hover:border-purple-300"><BarChart3 className="w-3.5 h-3.5 text-purple-500" /> Resultados</button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {modalAberto && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalAberto(false)}>
          <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 flex-1">{editId ? 'Editar ciclo' : 'Novo ciclo'}</h3>
              <button onClick={() => setModalAberto(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className={labelCls}>Nome *</label>
                <input className={inputCls} value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Avaliação anual 2026" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tipo</label>
                  <select className={inputCls} value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value as AvaliacaoCicloTipo }))}>
                    {(Object.keys(TIPO_LABEL) as AvaliacaoCicloTipo[]).map(k => <option key={k} value={k}>{TIPO_LABEL[k]}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Formulário *</label>
                  <select className={inputCls} value={form.formulario_id} onChange={e => setForm(p => ({ ...p, formulario_id: e.target.value }))}>
                    <option value="">Selecione...</option>
                    {formularios.map(f => <option key={f._id} value={f._id}>{f.nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Início</label>
                  <input className={inputCls} type="date" value={form.data_inicio} onChange={e => setForm(p => ({ ...p, data_inicio: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Fim</label>
                  <input className={inputCls} type="date" value={form.data_fim} onChange={e => setForm(p => ({ ...p, data_fim: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Departamentos (separados por vírgula)</label>
                  <input className={inputCls} value={form.departamentos} onChange={e => setForm(p => ({ ...p, departamentos: e.target.value }))} placeholder="01, 02" />
                </div>
                <div>
                  <label className={labelCls}>Cargos (separados por vírgula)</label>
                  <input className={inputCls} value={form.cargos} onChange={e => setForm(p => ({ ...p, cargos: e.target.value }))} placeholder="Vendedor, Analista" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Inclui</label>
                <div className="flex flex-wrap gap-4">
                  {([
                    ['autoavaliacao', 'Autoavaliação'],
                    ['gestor', 'Gestor'],
                    ['pares', 'Pares'],
                    ['liderados', 'Liderados'],
                  ] as [keyof FormState['inclui'], string][]).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={form.inclui[k]} onChange={e => setForm(p => ({ ...p, inclui: { ...p.inclui, [k]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <FeedbackBanner feedback={fb} />

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModalAberto(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ciclos;
